import * as React from 'react';
import { Link, graphql, navigate } from 'gatsby';
import { withLayout } from '../components/layout';
import { Container, Grid, Header, List } from 'semantic-ui-react';
import { findBestMatch } from 'string-similarity';

interface PageProps {
  data: {
    allSitePage: {
      edges: { node: { path: string } }[];
    };
    site: {
      siteMetadata: {
        salvusDocVersions: {
          current: string;
        };
      };
    };
  };
}

function stripTrailingSlash(x: string): string {
  if (x.slice(-1) == '/') {
    return x.slice(0, -1);
  }
  return x;
}

class Page404 extends React.Component<PageProps, {}> {
  public render() {
    let alternativePages: string[] = [];
    // S3 is configured to reroute every page it does not know to
    // `/404/#!/original_path`
    if (
      typeof window !== `undefined` &&
      window.location.hash &&
      window.location.hash.startsWith('#!/')
    ) {
      // All available pages.
      const allPages = this.props.data.allSitePage.edges.map(x =>
        stripTrailingSlash(x.node.path)
      );
      // Originally desired page.
      const originalPath = stripTrailingSlash(window.location.hash.slice(2));
      const currentSalvusVersion = this.props.data.site.siteMetadata
        .salvusDocVersions.current;

      // If it is a doc page, try replacing the version with the latest version
      // and see if it is a page. If yes, print a message and reroute.
      if (originalPath.startsWith('/')) {
        const splitPath = originalPath.split('/');
        const trialPath =
          splitPath.slice(0, 2).join('/') +
          `/${currentSalvusVersion}/` +
          splitPath.slice(3).join('/');
        if (allPages.includes(trialPath)) {
          // Navigate to new page in a bit.
          setTimeout(() => navigate(trialPath, { replace: true }), 5000);
          // Print message.
          return (
            <Container text textAlign="center">
              <Grid centered>
                <Grid.Row>
                  You are trying to access an outdated documentation version.
                </Grid.Row>
                <Grid.Row>
                  Redirecting to the latest version located at
                  <Link
                    style={{ paddingLeft: '1ex', paddingRight: '1ex' }}
                    to={trialPath}
                  >
                    {trialPath}
                  </Link>
                  in a few seconds...
                </Grid.Row>
                <Grid.Row>
                  If it does not redirect, please click on the link.
                </Grid.Row>
              </Grid>
            </Container>
          );
        }
      }

      let matches = findBestMatch(originalPath, allPages).ratings;
      matches.sort((a, b) => (a.rating > b.rating ? 1 : -1));
      matches.reverse();
      // Find 5 best matches as long as they are over a certain threshold.
      matches = matches.filter(x => x.rating > 0.6).slice(0, 5);
      alternativePages = matches.map(x => x.target);
    }
    return (
      <Container text textAlign="center">
        {/* Based on the MIT licensed https://codepen.io/lbebber/pen/EVxvWN/ */}
        <svg
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          width="250px"
          height="120px"
          xmlSpace="preserve"
          style={{ overflow: 'visible !important' }}
        >
          <defs>
            <pattern
              id="water"
              width=".25"
              height="1.1"
              patternContentUnits="objectBoundingBox"
            >
              <path
                fill="#C900C5"
                d="M0.25,1H0c0,0,0-0.659,0-0.916c0.083-0.303,0.158,0.334,0.25,0C0.25,0.327,0.25,1,0.25,1z"
              />
            </pattern>

            <text
              id="text"
              transform="translate(2,116)"
              fontFamily="'Cabin Condensed'"
              fontSize="161.047"
            >
              404
            </text>

            <mask id="text-mask">
              <use x="0" y="0" xlinkHref="#text" opacity="1" fill="#ffffff" />
            </mask>

            <g id="eff">
              <use x="0" y="0" xlinkHref="#text" fill="#FFC912" />

              <rect
                className="water-fill"
                mask="url(#text-mask)"
                fill="url(#water)"
                x="-300"
                y="50"
                width="1200"
                height="120"
                opacity="0.3"
              >
                <animate
                  attributeType="xml"
                  attributeName="x"
                  from="-300"
                  to="0"
                  repeatCount="indefinite"
                  dur="2s"
                />
              </rect>
              <rect
                className="water-fill"
                mask="url(#text-mask)"
                fill="url(#water)"
                y="45"
                width="1600"
                height="120"
                opacity="0.3"
              >
                <animate
                  attributeType="xml"
                  attributeName="x"
                  from="-400"
                  to="0"
                  repeatCount="indefinite"
                  dur="3s"
                />
              </rect>

              <rect
                className="water-fill"
                mask="url(#text-mask)"
                fill="url(#water)"
                y="55"
                width="800"
                height="120"
                opacity="0.3"
              >
                <animate
                  attributeType="xml"
                  attributeName="x"
                  from="-200"
                  to="0"
                  repeatCount="indefinite"
                  dur="1.4s"
                />
              </rect>
              <rect
                className="water-fill"
                mask="url(#text-mask)"
                fill="url(#water)"
                y="55"
                width="2000"
                height="120"
                opacity="0.3"
              >
                <animate
                  attributeType="xml"
                  attributeName="x"
                  from="-500"
                  to="0"
                  repeatCount="indefinite"
                  dur="2.8s"
                />
              </rect>
            </g>
          </defs>

          <use xlinkHref="#eff" opacity="0.9" />
        </svg>

        <Header as="h1">Page not found</Header>
        <p>Cannot find the wiggle you are looking for?</p>
        {alternativePages && (
          <div>
            <div>Maybe you are looking for one these pages?</div>
            <List>
              {alternativePages.map((x, i) => (
                <List.Item key={i}>
                  <Link to={x}>{x}</Link>
                </List.Item>
              ))}
            </List>
          </div>
        )}
      </Container>
    );
  }
}

export default withLayout(Page404);

export const query = graphql`
  query {
    allSitePage {
      edges {
        node {
          path
        }
      }
    }
    site {
      siteMetadata {
        salvusDocVersions {
          current
        }
      }
    }
  }
`;
